/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { action, computed } from 'mobx';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import DashboardStore from 'stores/DashboardStore';
import WidgetStore from 'stores/WidgetStore';

import type { CommonWidget } from '../../commonTypes';

import styles from './WidgetActions.module.scss';

export type WidgetActionsProps = CommonWidget;

type InjectedProps = {
  dashboardStore: DashboardStore,
  widgetStore: WidgetStore,
} & TranslatorProps;

@translate()
@inject('dashboardStore', 'widgetStore')
@observer
export default class WidgetActions extends ComponentWithInjectedProps<
  WidgetActionsProps,
  {},
  InjectedProps
> {
  @computed get isFavourite(): boolean {
    const {
      widgetStore: { favouriteWidgets },
      widgetItem,
    } = this.props;

    return favouriteWidgets.includes(widgetItem.widget);
  }

  @action
  handleFavouriteButtonClick = () => {
    const {
      widgetStore,
      widgetItem: { widget },
    } = this.props;

    const newState = !widgetStore.favouriteWidgets.includes(widget);
    widgetStore.favouriteWidgets = newState
      ? [...widgetStore.favouriteWidgets, widget]
      : widgetStore.favouriteWidgets.filter(favouriteWidget => favouriteWidget !== widget);
  };

  @action
  handleFullScreenButtonClick = () => {
    if (this.props.isFullScreen) {
      this.props.dashboardStore.fullScreenWidget = null;
    } else {
      this.props.dashboardStore.fullScreenWidget = this.props.widgetItem.widget;
    }
  };

  @action
  handleAddWidgetButtonClick = () => {
    this.props.widgetStore.widgets.push({
      widget: 'empty',
      position: this.props.widgetItem.position,
      variant: 'default',
    });
  };

  @action
  handleRemoveWidgetButtonClick = () => {
    const { widgetStore, widgetItem } = this.props;

    const widgetToRemoveIndex = widgetStore.widgets.findIndex(
      widget => widgetItem.position === widget.position
    );

    if (widgetToRemoveIndex > -1) {
      widgetStore.widgets.splice(widgetToRemoveIndex, 1, {
        widget: 'empty',
        position: widgetItem.position,
        variant: 'default',
      });

      widgetStore.widgets = widgetStore.widgets;
    }
  };

  @action
  handleChangeWidgetPositionClick = direction => {
    const {
      widgetStore,
      widgetStore: { widgets },
      widgetItem,
    } = this.props;

    if (direction === 'right') {
      const nextWidget = widgets.filter(widget => widget.position === widgetItem.position + 1)[0];
      const currentWidget = { ...widgetItem, position: widgetItem.position + 1 };
      widgetStore.widgets = widgets.map(widget => {
        if (widget.widget === currentWidget.widget) return { ...widget, ...currentWidget };
        if (widget.widget === nextWidget.widget)
          return { ...widget, position: nextWidget.position - 1 };
        return widget;
      });
    }

    if (direction === 'left') {
      const prevWidget = widgets.filter(widget => widget.position === widgetItem.position - 1)[0];
      const currentWidget = { ...widgetItem, position: widgetItem.position - 1 };
      widgetStore.widgets = widgets.map(widget => {
        if (widget.widget === currentWidget.widget) return { ...widget, ...currentWidget };
        if (widget.widget === prevWidget.widget)
          return { ...widget, position: prevWidget.position + 1 };
        return widget;
      });
    }
  };

  renderFavouriteButton = () => {
    const { size } = this.props;

    if (size === 'thumbnail') {
      return (
        <IconButton onClick={this.handleFavouriteButtonClick}>
          {this.isFavourite ? (
            <svg className={styles.buttonIcon}>
              <use xlinkHref="#icon-star-fill" />
            </svg>
          ) : (
            <svg className={styles.buttonIcon}>
              <use xlinkHref="#icon-star-empty" />
            </svg>
          )}
        </IconButton>
      );
    }
  };

  renderActiveWidgetButtons = () => {
    const { widgetItem, size, isFullScreen } = this.props;
    const buttons: React.Node[] = [];

    if (size !== 'thumbnail') {
      buttons.push(
        <Button
          key={`${widgetItem.position}${widgetItem.widget}-fullscreen-button`}
          color="secondary"
          variant="fab"
          mini={true}
          onClick={this.handleFullScreenButtonClick}
          className={styles['widget-circle-button']}
        >
          <svg>
            <use xlinkHref={`${isFullScreen ? '#icon-close' : '#icon-full-screen'}`} />
          </svg>
        </Button>
      );

      if (!isFullScreen) {
        if (widgetItem.position < 4 && widgetItem.position > 0) {
          buttons.push(
            <Button
              key={`${widgetItem.position}${widgetItem.widget}-expand-button`}
              color="secondary"
              variant="fab"
              mini={true}
              onClick={() => this.handleChangeWidgetPositionClick('left')}
              className={`${styles['widget-circle-button']} ${styles['chevron-left']}`}
            >
              <svg>
                <use xlinkHref="#icon-chevron-up" />
              </svg>
            </Button>
          );
        }
        if (widgetItem.position > -1 && widgetItem.position < 3) {
          buttons.push(
            <Button
              key={`${widgetItem.position}${widgetItem.widget}-expand-button`}
              color="secondary"
              variant="fab"
              mini={true}
              onClick={() => this.handleChangeWidgetPositionClick('right')}
              className={`${styles['widget-circle-button']} ${styles['chevron-right']}`}
            >
              <svg>
                <use xlinkHref="#icon-chevron-up" />
              </svg>
            </Button>
          );
        }
        buttons.push(
          <Button
            key={`${widgetItem.position}${widgetItem.widget}-close-button`}
            color="primary"
            variant="fab"
            mini={true}
            onClick={this.handleRemoveWidgetButtonClick}
            className={styles['widget-circle-button']}
          >
            <svg>
              <use xlinkHref="#icon-close" />
            </svg>
          </Button>
        );
      }
    }

    return buttons;
  };

  render() {
    return (
      <div className={styles['widget-actions']}>
        {this.renderFavouriteButton()}
        {this.renderActiveWidgetButtons()}
      </div>
    );
  }
}
